import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import { Button } from '@cmsgov/design-system';
import { FormattedMessage } from 'react-intl';

// Components
import ModalHeader from '../headers/ModalHeader';

// utils
import { onLogout } from '@utils/localStorage/sessionStorageUtils';
export interface ISessionIdleModalData {
    logoutUrl: string;
    loggedOut: boolean;
    onHandleReset: () => void;
    getRemainingTime?: () => number;
}

export interface ISessionIdleWarningModalProps {
    actions: {
        hideModal: () => void;
    };
    bottomAnchors: Array<unknown>;
    boundAnchor: (anchor: unknown) => (input: HTMLElement | null) => void;
    modal: {
        modalData: ISessionIdleModalData;
    };
    modalHeaderProps: Record<string, unknown>;
}

const SessionIdleWarningModal = (props: ISessionIdleWarningModalProps): ReactElement => {
    const {
        actions: { hideModal },
        bottomAnchors: [anchor_1, anchor_2],
        boundAnchor,
        modal: {
            modalData: { logoutUrl, loggedOut, onHandleReset, getRemainingTime },
        },
        modalHeaderProps,
    } = props;

    const [counter, setCounter] = useState<number>(() => (getRemainingTime ? getRemainingTime() : 0));

    // The useSessionIdle hook owns the real expiry and logout (via react-idle-timer).
    // This countdown is purely presentational and reads the timer's wall-clock
    // remaining time, so it stays accurate even if the tab was throttled/backgrounded.
    useEffect(() => {
        if (loggedOut || !getRemainingTime) {
            return undefined;
        }
        setCounter(getRemainingTime());
        const interval = setInterval(() => {
            setCounter(getRemainingTime());
        }, 1000);
        return () => clearInterval(interval);
    }, [loggedOut, getRemainingTime]);

    const isTimeOver = loggedOut || counter <= 0;

    const handleLeavingApplication = (): void => {
        // onLogout() clears all session/local storage (including the impersonation
        // flags), so there's no need to reset those individually beforehand.
        onLogout();
        window.location.replace(logoutUrl);
    };

    const handleRestorationClick = (): void => {
        onHandleReset();
        hideModal();
    };

    return (
        <Fragment>
            <div className="gi-modal__header__head">
                <ModalHeader
                    {...{
                        ...modalHeaderProps,
                        hideModal: !isTimeOver ? handleRestorationClick : handleLeavingApplication,
                    }}
                />
            </div>
            <div className="gi-modal__body txt-left" id={`${modalHeaderProps?.modalId}_body`}>
                {!isTimeOver ? (
                    <Fragment>
                        <FormattedMessage
                            id="modals.session.will_be_logged_out"
                            values={{
                                seconds: counter,
                            }}
                            tagName="p"
                        />
                        <FormattedMessage id="modals.session.idle_continue" tagName="p" />
                    </Fragment>
                ) : (
                    <p role="alert">
                        <FormattedMessage id="modals.session.have_been_logged_out" />
                    </p>
                )}
            </div>
            <div className="gi-modal__controls">
                {!isTimeOver ? (
                    <Button
                        className="usa-button gi-button gi-button__primary"
                        onClick={handleRestorationClick}
                        inputRef={boundAnchor(!isTimeOver ? anchor_1 : {})}
                    >
                        <FormattedMessage id="button.ok" />
                    </Button>
                ) : (
                    <Button
                        className="usa-button gi-button gi-button__primary"
                        onClick={handleLeavingApplication}
                        inputRef={boundAnchor(isTimeOver ? anchor_2 : {})}
                    >
                        <FormattedMessage id="button.go_to_login" />
                    </Button>
                )}
            </div>
        </Fragment>
    );
};

export default SessionIdleWarningModal;
